import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Inbox, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import dayjs from 'dayjs';

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { PageContainer } from '../../../common/components/PageContainer';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { useSupportAdminApi } from '../../../common/hooks/use-support-api.hook';
import type { SupportTask, TaskStatus, TaskType } from '../../../common/models/support.model';
import { TASK_TYPES } from '../../../common/models/support.model';
import { onRealtime } from '../../../common/services/realtime.service';
import { TaskBoard } from '../../../modules/support/staff/TaskBoard';
import { TaskDialog, TaskFields, draftFromTask, payloadFromDraft, type TaskDraft } from '../../../modules/support/staff/TaskDialog';
import { CONVERSATION_STATUS_META, TASK_TYPE_META } from '../../../modules/support/support-meta';
import { AssigneeFilter } from '../../../modules/support/staff/StaffPeople';
import { useStaffName } from '../../../modules/support/staff/staff-support.store';

/** The support board: bugs, feature requests, issues and tasks, dragged from backlog to done. */
export const SupportBoardPage = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const api = useSupportAdminApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [params, setParams] = useSearchParams();

  const [tasks, setTasks] = useState<SupportTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<TaskType | 'all'>('all');
  const [search, setSearch] = useState('');
  // '' = everyone, 'none' = nobody, else a teammate's email.
  const [assignee, setAssignee] = useState('');
  const nameOf = useStaffName();
  const [creatingIn, setCreatingIn] = useState<TaskStatus | null>(null);
  const [openTask, setOpenTask] = useState<SupportTask | null>(null);
  const [draft, setDraft] = useState<TaskDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const tRef = useRef(t);
  tRef.current = t;

  const load = useCallback(async () => {
    try {
      const list = await apiRef.current.listTasks();
      if (list) setTasks(list);
    } catch (e: any) {
      toast.error(e?.message || tRef.current('SUPPORT.STAFF.LOAD_FAILED'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const offs = [
      onRealtime('support:task', (task: SupportTask) => setTasks((list) => [task, ...list.filter((x) => x._id !== task._id)])),
      onRealtime('support:task_removed', ({ _id }: { _id: string }) => setTasks((list) => list.filter((x) => x._id !== _id))),
    ];
    return () => offs.forEach((off) => off());
  }, [load]);

  // ?task=<id> (from the inbox) opens that task.
  const taskParam = params.get('task');
  useEffect(() => {
    if (!taskParam) return;
    apiRef.current.getTask(taskParam).then((task) => task && openSheet(task)).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskParam]);

  const openSheet = (task: SupportTask) => {
    setOpenTask(task);
    setDraft(draftFromTask(task));
    // Linked conversations come with the full task.
    apiRef.current.getTask(task._id).then((full) => full && setOpenTask(full)).catch(() => undefined);
  };

  const closeSheet = () => {
    setOpenTask(null);
    setDraft(null);
    if (params.get('task')) {
      params.delete('task');
      setParams(params, { replace: true });
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks.filter((task) => {
      if (typeFilter !== 'all' && task.type !== typeFilter) return false;
      if (assignee && (assignee === 'none' ? !!task.assignee : task.assignee !== assignee)) return false;
      if (!q) return true;
      return (
        `#${task.number}` === q ||
        task.title.toLowerCase().includes(q) ||
        (task.vendorName || '').toLowerCase().includes(q) ||
        task.labels.some((l) => l.toLowerCase().includes(q)) ||
        (task.assignee || '').toLowerCase().includes(q) ||
        (task.assignee ? nameOf(task.assignee).toLowerCase().includes(q) : false)
      );
    });
  }, [tasks, typeFilter, search, assignee, nameOf]);

  const move = async (id: string, m: { status: TaskStatus; beforeId?: string; afterId?: string }) => {
    // Show the move at once; the response carries the real order.
    const before = tasks;
    const above = tasks.find((x) => x._id === m.beforeId);
    const below = tasks.find((x) => x._id === m.afterId);
    const order = above && below ? (above.order + below.order) / 2 : above ? above.order + 1024 : below ? below.order - 1024 : Date.now();
    setTasks((list) => list.map((x) => (x._id === id ? { ...x, status: m.status, order } : x)));
    try {
      const saved = await apiRef.current.moveTask(id, m);
      if (saved) setTasks((list) => list.map((x) => (x._id === id ? saved : x)));
    } catch (e: any) {
      setTasks(before);
      toast.error(e?.message || t('SUPPORT.STAFF.ACTION_FAILED'));
    }
  };

  const save = async () => {
    if (!openTask || !draft) return;
    if (!draft.title.trim()) {
      toast.error(t('SUPPORT.TASK.TITLE_REQUIRED'));
      return;
    }
    setSaving(true);
    try {
      const saved = await apiRef.current.updateTask(openTask._id, payloadFromDraft(draft));
      if (saved) setTasks((list) => list.map((x) => (x._id === saved._id ? saved : x)));
      toast.success(t('SUPPORT.BOARD.SAVED'));
      closeSheet();
    } catch (e: any) {
      toast.error(e?.message || t('SUPPORT.TASK.SAVE_FAILED'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!openTask) return;
    try {
      await apiRef.current.deleteTask(openTask._id);
      setTasks((list) => list.filter((x) => x._id !== openTask._id));
      setConfirmDelete(false);
      closeSheet();
    } catch (e: any) {
      toast.error(e?.message || t('SUPPORT.STAFF.ACTION_FAILED'));
    }
  };

  const counts = useMemo(() => Object.fromEntries(TASK_TYPES.map((ty) => [ty, tasks.filter((x) => x.type === ty && x.status !== 'done').length])), [tasks]);

  return (
    <PageContainer
      breadcrumbs={[{ label: t('SUPPORT.STAFF.SECTION') }, { label: t('SUPPORT.STAFF.BOARD') }]}
      status={
        <Button asChild variant="outline" size="sm">
          <Link to="/support"><Inbox data-icon="inline-start" />{t('SUPPORT.STAFF.INBOX')}</Link>
        </Button>
      }
    >
      <div className="flex h-[calc(100vh-8.5rem)] min-h-[560px] flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="me-2 font-heading text-2xl font-medium tracking-tight">{t('SUPPORT.BOARD.TITLE')}</h1>
          <div className="flex gap-1 rounded-xl bg-muted p-1" role="tablist" aria-label={t('SUPPORT.TASK.TYPE')}>
            {(['all', ...TASK_TYPES] as const).map((ty) => {
              const meta = ty === 'all' ? null : TASK_TYPE_META[ty];
              const Icon = meta?.icon;
              return (
                <button
                  key={ty}
                  type="button"
                  role="tab"
                  aria-selected={typeFilter === ty}
                  onClick={() => setTypeFilter(ty)}
                  className={cn('flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs', typeFilter === ty ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground hover:text-foreground')}
                >
                  {Icon && <Icon className={cn('size-3.5', typeFilter === ty && meta.tone)} />}
                  {ty === 'all' ? t('SUPPORT.BOARD.ALL') : t(meta.labelKey)}
                  {ty !== 'all' && counts[ty] > 0 && <span className="tabular-nums text-muted-foreground">{counts[ty]}</span>}
                </button>
              );
            })}
          </div>
          <AssigneeFilter value={assignee} onChange={setAssignee} me={user?.email} className="ms-auto h-8 w-36" />
          <div className="relative w-56">
            <Search className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('SUPPORT.BOARD.SEARCH')} className="h-8 ps-8" aria-label={t('SUPPORT.BOARD.SEARCH')} />
          </div>
          <Button variant="outline" onClick={() => setCreatingIn('todo')}>
            <Plus data-icon="inline-start" />
            {t('SUPPORT.BOARD.NEW')}
          </Button>
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center"><Spinner /></div>
        ) : (
          <TaskBoard tasks={visible} onMove={move} onOpen={openSheet} onAdd={(s) => setCreatingIn(s)} />
        )}
      </div>

      <TaskDialog
        open={!!creatingIn}
        onOpenChange={(o) => !o && setCreatingIn(null)}
        title={t('SUPPORT.BOARD.NEW')}
        me={user?.email}
        initial={draftFromTask({ status: creatingIn || 'todo', assignee: user?.email, visibleToVendor: false })}
        onSubmit={async (payload) => {
          const created = await apiRef.current.createTask(payload);
          if (created) setTasks((list) => [created, ...list.filter((x) => x._id !== created._id)]);
        }}
      />

      <Sheet open={!!openTask} onOpenChange={(o) => !o && closeSheet()}>
        <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-lg">
          {openTask && draft && (
            <>
              <SheetHeader className="border-b">
                <SheetTitle className="flex items-center gap-2">
                  <span className="tabular-nums text-muted-foreground">#{openTask.number}</span>
                  <span className="truncate">{openTask.title}</span>
                </SheetTitle>
                <SheetDescription>
                  {t('SUPPORT.BOARD.CREATED_BY', { who: nameOf(openTask.createdBy), when: dayjs(openTask.createdAt).format('D MMM YYYY') })}
                  {openTask.vendorName ? ` · ${openTask.vendorName}` : ''}
                </SheetDescription>
              </SheetHeader>
              <div className="flex flex-col gap-5 p-4">
                <TaskFields draft={draft} onChange={setDraft} linkedToVendor={openTask.conversations.length > 0} me={user?.email} />
                {!!openTask.linkedConversations?.length && (
                  <div className="flex flex-col gap-1.5">
                    <p className="text-xs font-medium text-muted-foreground">{t('SUPPORT.BOARD.LINKED')}</p>
                    {openTask.linkedConversations.map((c) => (
                      <Link key={c._id} to={`/support?c=${c._id}`} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-xs hover:bg-muted/60">
                        <span className={cn('size-1.5 shrink-0 rounded-full', CONVERSATION_STATUS_META[c.status].dot)} />
                        <span className="min-w-0 flex-1 truncate">{c.subject}</span>
                        <span className="shrink-0 text-muted-foreground">{c.vendorName}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <SheetFooter className="mt-auto flex-row justify-between border-t">
                <Button
                  variant="outline"
                  className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 data-icon="inline-start" />
                  {t('SUPPORT.BOARD.DELETE')}
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={closeSheet} disabled={saving}>{t('SUPPORT.ACTIONS.CANCEL')}</Button>
                  <Button variant="outline" className="border-foreground" onClick={save} disabled={saving}>
                    {saving && <Spinner data-icon="inline-start" />}
                    {t('SUPPORT.ACTIONS.SAVE')}
                  </Button>
                </div>
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('SUPPORT.BOARD.DELETE_TITLE', { number: openTask?.number })}</AlertDialogTitle>
            <AlertDialogDescription>{t('SUPPORT.BOARD.DELETE_BODY')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('SUPPORT.ACTIONS.CANCEL')}</AlertDialogCancel>
            <AlertDialogAction
              variant="outline"
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={(e) => { e.preventDefault(); remove(); }}
            >
              {t('SUPPORT.BOARD.DELETE')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
};

export default SupportBoardPage;
